<template>
  <div class="drop-zone-container">
    <div v-if="previewFile" class="preview-container">
      <div class="preview-item">
        <div class="preview-wrapper">
          <img
              v-if="isImage(previewFile)"
              :src="getPreviewUrl(previewFile)"
              :alt="previewFile.name || previewFile.original_name"
              class="preview-image"
              @click="triggerFileInput"
          />
          <div v-else class="file-preview" @click="triggerFileInput">
            <v-icon>$file</v-icon>
            <div class="file-name">{{ previewFile.name || previewFile.original_name }}</div>
          </div>
          <button
              class="remove-button"
              @click.stop="removeFile"
              aria-label="Удалить файл"
          >
            <v-icon color="#A6ADB5" size="16">$close</v-icon>
          </button>
        </div>
      </div>
    </div>

    <div
        v-show="!previewFile"
        class="drop-zone"
        :class="{
        'drop-zone--disabled': disabled,
        'drop-zone--enabled': !disabled,
        'drop-zone--error': fileError?.length,
        'drop-zone--over': !disabled && dragOver
      }"
        role="button"
        @click="triggerFileInput"
        @dragenter="incrementDragCounter"
        @dragleave="decrementDragCounter"
        @dragover.prevent="addDragOverClass"
        @drop.prevent="handleDrop"
    >
      <div class="icon-block d-flex justify-center align-center mb-1">
        <v-icon>$upload</v-icon>
      </div>
      <div class="drop-zone__label">{{ label }}</div>
      <input
          type="file"
          name="myFiles"
          class="drop-zone__input"
          @change="handleFileChange"
          ref="fileInput"
          :multiple="multiple"
          :accept="accept"
          :disabled="disabled"
      >
    </div>
  </div>
</template>

<script>
export default  {
  name: "m-drag-n-drop-file-uploader",
  props: {
    accept: {
      type: String,
      default: 'image/*, audio/*, video/*',
    },
    label: {
      type: String,
      default: 'Нажмите, чтобы добавить или перетащите файлы',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fileError: {
      type: String,
      default: undefined
    },
    value: {
      type: [File, Object, null],
      default: null
    }
  },
  data() {
    return {
      dragOver: false,
      dragCounter: 0,
      previewFile: null
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue === null) {
          this.previewFile = null;
        } else if (newValue instanceof File) {
          this.processFile(newValue);
        } else if (newValue && typeof newValue === 'object') {
          this.previewFile = newValue;
        }
      }
    }
  },
  methods: {
    triggerFileInput() {
      if (!this.disabled) {
        this.$refs.fileInput.click();
      }
    },
    handleFileChange(event) {
      const files = event.target.files;
      if (files.length) {
        this.processFile(files[0]);
        this.$emit('input', files[0]);
        this.$emit('uploadFiles', files[0]);
      }
      event.target.value = '';
    },
    addDragOverClass() {
      if (!this.disabled) {
        this.dragOver = true
      }
    },
    incrementDragCounter() {
      if (!this.disabled) {
        this.dragCounter++;
        this.dragOver = true;
      }
    },
    decrementDragCounter() {
      if (!this.disabled) {
        this.dragCounter--;
        if (this.dragCounter === 0) {
          this.dragOver = false;
        }
      }
    },
    handleDrop(event) {
      if (!this.disabled) {
        const files = event.dataTransfer.files;
        if (files.length) {
          this.processFile(files[0]);
          this.$emit('input', files[0]);
          this.$emit('uploadFiles', files[0]);
        }
        this.dragCounter = 0;
        this.dragOver = false;
      }
    },
    processFile(file) {
      const fileData = {
        file: file,
        name: file.name,
        type: file.type,
        preview: null
      };

      if (this.isImage(file)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileData.preview = e.target.result;
          this.previewFile = fileData;
          this.$emit('previewFileChanged', fileData);
        };
        reader.readAsDataURL(file);
      } else {
        this.previewFile = fileData;
        this.$emit('previewFileChanged', fileData);
      }
    },
    isImage(file) {
      const fileType = file?.type || file?.file?.type;
      return fileType?.startsWith('image/');
    },
    getPreviewUrl(file) {
      if (file.preview) {
        return file.preview;
      }

      if (file.preview_file) {
        if (file.is_local) {
            return `${this.$store.getters['settings/BACKEND_URL']}/${file.preview_file}`;
        } else {
            return file.preview_file
        }
         
      }

      if (file.path && this.isImage(file)) {
        if (file.is_local) {
            return `${this.$store.getters['settings/BACKEND_URL']}/${file.path}`;
        }
        return file.path
      }

      return '';
    },
    removeFile() {
      this.previewFile = null;
      this.$emit('input', null);
      this.$emit('fileRemoved');
      this.$emit('previewFileChanged', null);
    }
  }
};
</script>

<style scoped lang="scss">
/* Стили остаются без изменений */
.drop-zone-container {
  width: 100%;
}

.preview-container {
  width: 100%;
}

.preview-item {
  position: relative;
  width: 100%;
  min-height: 100px;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: var(--bg);
  border: 2px dotted var(--basic-grey);
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
}

.file-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  cursor: pointer;
  padding: 8px;

  .file-name {
    font-size: 10px;
    text-align: center;
    margin-top: 4px;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.remove-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4.5px;
  background: var(--bg);
  color: var(--basic-grey);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #ff4444;
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }
}

.drop-zone {
  min-width: 124px;
  width: 100%;
  min-height: 100px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 6px;
  border: 2px dotted var(--basic-grey);
  background-color: var(--bg);
  flex-direction: column;
  cursor: pointer;

  &__label {
    font-family: Open Sans;
    font-weight: 400;
    font-style: Regular;
    font-size: 11px;
    line-height: 130%;
    letter-spacing: 0%;
    color: var(--basic-grey);
  }
}

.icon-block {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--another-grey);
}

.drop-zone--enabled:hover {
  border-color: #8a8a8a;

  .drop-zone__label {
    color: #8a8a8a;
  }
}

.drop-zone--over {
  border: 2px solid var(--basic-grey);
  background-color: #f0f0f0;
}

.drop-zone--enabled {
  border: 2px dotted var(--basic-grey);
}

.drop-zone--disabled {
  border: 4px dashed darkgrey;
  cursor: default;
  opacity: 0.6;
}

.drop-zone__input {
  display: none;
}

.drop-zone--error {
  border: 4px dashed #E57373;
  color: #E57373;
}
</style>